import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";

import { getBalanceAndPin } from "../../features/balance-and-pin/balanceAndPinSlice";
import Spinner from "../../components/Spinner";
import {
  IntroSection,
  MainContainer,
  SectionContainer,
  Footer
} from "../hsbc-26/styles";
import { PinModalOverlay } from "../priceless-26-mb/styles";

import hsbcLogo from "../hsbc-26/images/logo.svg";
import wearable from "../hsbc-26/images/wearable.png";
import digiseqLogo from "../hsbc-26/images/digiseq-logo.png";

const ViewPinButton = styled.button`
  background: transparent;
  border: 1px solid #fff;
  border-radius: 4px;
  color: #fff;
  font-family: "Mark For MC Narrow W00 Light";
  font-size: 16px;
  font-weight: 350;
  line-height: 24px;
  letter-spacing: 0.5px;
  padding: 4px 6px;
  cursor: pointer;
`;

const Priceless26Singapore = () => {
  const [searchParams] = useSearchParams();
  const serialNumber = searchParams.get("serialNumber");
  const provider = searchParams.get("provider");
  const defaultRate = parseFloat(searchParams.get("rate"));
  const rateSGD = parseFloat(searchParams.get("rateSGD")) || defaultRate;

  const [isPinOpen, setIsPinOpen] = useState(false);

  const formatCurrency = (usdAmount) => {
    const usdFormatted = `US$${usdAmount}`;
    if (!rateSGD) return usdFormatted;
    const localAmount = (parseFloat(usdAmount) * rateSGD).toFixed(2);
    return `${usdFormatted} / S$${localAmount}`;
  };

  const dispatch = useDispatch();
  const { balanceAndPin, isLoading } = useSelector(
    (state) => state.balanceAndPin
  );

  useEffect(() => {
    if (!balanceAndPin && serialNumber) {
      dispatch(
        getBalanceAndPin({
          serial: serialNumber,
          query: `?provider=${provider || "much_better"}`
        })
      );
    }
  }, [balanceAndPin, serialNumber, dispatch, provider]);

  if (isLoading) return <Spinner />;

  return (
    <MainContainer>
      <IntroSection>
        <div className="intro-logo">
          <img src={hsbcLogo} alt="HSBC Mastercard" />
        </div>
        <h1>Welcome to your Guns N&apos; Roses Priceless Experience</h1>
        <div className="intro-content">
          <p>
            We’re thrilled to have you here — get ready for an unforgettable
            evening!
          </p>
          <p>
            Your <strong>HSBC Mastercard keyring wearable</strong> is preloaded
            with {formatCurrency(25)} to make your night even more special.
          </p>
          <p>
            Whether it’s a drink, a snack, or a little something extra, just{" "}
            <strong>tap and enjoy!</strong>
          </p>
        </div>
      </IntroSection>
      <SectionContainer>
        <div className="balance-container">
          <h2>Your Balance</h2>
          <p className="balance-value">
            {balanceAndPin?.balanceDetails?.availableBalance
              ? formatCurrency(balanceAndPin.balanceDetails.availableBalance)
              : "N/A"}
          </p>
          <p>
            <i>
              Your balance will update automatically after each transaction.
            </i>
          </p>
          <ViewPinButton type="button" onClick={() => setIsPinOpen(true)}>
            View PIN
          </ViewPinButton>
        </div>
      </SectionContainer>
      <SectionContainer>
        <div className="how-it-works">
          <div className="wearable-container">
            <img src={wearable} alt="wearable" />
          </div>
          <h2>How It Works</h2>
          <p>
            Your keyring is preloaded with <strong>{formatCurrency(25)}</strong>.
            Simply{" "}
            <strong>tap it on any contactless payment terminal</strong> where
            Mastercard Contactless is enabled to make a purchase.
          </p>
          <p>
            If you make a purchase in a currency other than US$, the amount will
            be converted using the{" "}
            <strong>
              foreign exchange rate in effect on the date of the transaction.
            </strong>{" "}
            Any unused balance will remain available for use at any merchants
            after the event.
          </p>
        </div>
      </SectionContainer>
      <SectionContainer>
        <div className="priceless-link-container">
          <p>
            Tap the button below to discover more HSBC Singapore Priceless
            Experiences
          </p>
          <a
            href="https://www.priceless.com/HSBC-Singapore?ac=HSBCSG"
            target="_blank"
            rel="noreferrer"
          >
            <div className="priceless-link">Explore now</div>
          </a>
        </div>
      </SectionContainer>

      <Footer>
        <p>Powered by</p>
        <img
          src={digiseqLogo}
          alt="digiseq"
          onClick={() =>
            window.open(
              "https://www.digiseq.co.uk",
              "_blank noreferrer noopener"
            )
          }
        />
      </Footer>

      {isPinOpen && (
        <PinModalOverlay onClick={() => setIsPinOpen(false)}>
          <div className="pin-modal" onClick={(e) => e.stopPropagation()}>
            <button className="pin-close" onClick={() => setIsPinOpen(false)}>
              ×
            </button>
            <p className="pin-label">Your PIN</p>
            <p className="pin-value">
              {balanceAndPin?.pinDetails?.pin || "N/A"}
            </p>
          </div>
        </PinModalOverlay>
      )}
    </MainContainer>
  );
};

export default Priceless26Singapore;
